import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * Transient confirmations ("Payment recorded") and failures of background actions.
 * Never use a toast for form validation: that is an inline message under the field.
 */

export type ToastTone = 'success' | 'error' | 'info';

export interface ToastOptions {
  title: string;
  description?: string;
  tone?: ToastTone;
  /** e.g. { label: 'View', onSelect } or Undo. */
  action?: { label: string; onSelect: () => void };
  durationMs?: number;
}

interface ToastItem extends ToastOptions {
  id: number;
  tone: ToastTone;
}

interface ToastApi {
  show: (options: ToastOptions) => void;
  success: (title: string, options?: Omit<ToastOptions, 'title' | 'tone'>) => void;
  error: (title: string, options?: Omit<ToastOptions, 'title' | 'tone'>) => void;
  info: (title: string, options?: Omit<ToastOptions, 'title' | 'tone'>) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const MAX_VISIBLE = 4;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const show = useCallback((options: ToastOptions) => {
    const id = nextId.current++;
    setToasts((current) => [...current.slice(-(MAX_VISIBLE - 1)), { ...options, id, tone: options.tone ?? 'info' }]);
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      show,
      success: (title, options) => show({ ...options, title, tone: 'success' }),
      error: (title, options) => show({ ...options, title, tone: 'error' }),
      info: (title, options) => show({ ...options, title, tone: 'info' }),
    }),
    [show],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="pointer-events-none fixed inset-x-0 bottom-0 z-[100] flex flex-col items-end gap-2 p-4 pb-20 sm:pb-4" aria-label="Notifications">
        {toasts.map((toast) => (
          <ToastCard key={toast.id} toast={toast} onDismiss={() => dismiss(toast.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

const toneStyles: Record<ToastTone, { icon: typeof Info; iconClass: string }> = {
  success: { icon: CheckCircle2, iconClass: 'text-emerald-500' },
  error: { icon: AlertCircle, iconClass: 'text-red-500' },
  info: { icon: Info, iconClass: 'text-sky-500' },
};

function ToastCard({ toast, onDismiss }: { toast: ToastItem; onDismiss: () => void }) {
  const [paused, setPaused] = useState(false);
  const { icon: Icon, iconClass } = toneStyles[toast.tone];
  const duration = toast.durationMs ?? (toast.tone === 'error' ? 8000 : 5000);

  useEffect(() => {
    if (paused) {
      return;
    }
    const id = window.setTimeout(onDismiss, duration);
    return () => window.clearTimeout(id);
  }, [paused, duration, onDismiss]);

  return (
    <div
      role={toast.tone === 'error' ? 'alert' : 'status'}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      className={cn('pointer-events-auto flex w-full max-w-sm animate-slide-up items-start gap-3 rounded-xl bg-slate-900 p-3.5 text-white shadow-pop')}
    >
      <Icon className={cn('mt-0.5 size-5 shrink-0', iconClass)} aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold">{toast.title}</p>
        {toast.description && <p className="mt-0.5 text-xs text-slate-300">{toast.description}</p>}
      </div>
      {toast.action && (
        <button
          type="button"
          onClick={() => {
            toast.action?.onSelect();
            onDismiss();
          }}
          className="rounded px-2 py-1 text-xs font-semibold text-amethyst-200 hover:bg-white/10"
        >
          {toast.action.label}
        </button>
      )}
      <button type="button" aria-label="Dismiss notification" onClick={onDismiss} className="rounded p-1 text-slate-400 hover:bg-white/10 hover:text-white">
        <X className="size-4" aria-hidden />
      </button>
    </div>
  );
}

export function useToast(): ToastApi {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used inside <ToastProvider>');
  }
  return context;
}
